import { bucketOfTask, type BucketableTask } from '../agent-task-buckets';
import { notBeforeMs } from '../task-schedule';
import type { BookingClient, CalendarItem, CalendarPerson } from './calendar-item';

// What the calendar draws, from each source's answer. The connected calendars and Bookings come from
// the harness already drawn (`/api/calendar/*`); what is left here is the part only the reader's own
// device can do (which local day an all-day item is on, the provider's page for that day) and the
// Symphony layers, whose rows every client already reads.

const HOUR = 3_600_000;

/** A meeting or a booking, as `/api/calendar/meetings` and `/api/calendar/bookings` send it. */
export interface WireCalendarItem {
  key: string;
  kind: 'meeting' | 'booking';
  title: string;
  /** ISO instants. */
  start: string;
  end: string;
  /** An all-day item's first day and the day after its last, as calendar dates. */
  allDay?: { startDate: string; endDate: string };
  sourceId: string;
  sourceLabel?: string;
  calendar?: { id: string; name: string; primary?: boolean };
  source?: { provider?: string; logoUrl?: string };
  color?: string;
  rsvp?: CalendarItem['rsvp'];
  booking?: { clients: BookingClient[] };
  needsYou?: boolean;
  settled?: boolean;
  status?: string;
  location?: string;
  people?: CalendarPerson[];
  staff?: string[];
  capacity?: { booked: number; total: number };
  videoLink?: string;
  movable?: boolean;
  href?: string;
  ref: Record<string, string>;
}

/** Local midnight of a calendar date. */
function localDay(date: string): number {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day).getTime();
}

/** The provider's own calendar, opened on the reader's local day. */
function providerDayLink(provider: string | undefined, at: number): string | undefined {
  const day = new Date(at);
  if (provider === 'GOOGLE') {
    return `https://calendar.google.com/calendar/r/day/${day.getFullYear()}/${day.getMonth() + 1}/${day.getDate()}`;
  }
  if (provider === 'MICROSOFT') return 'https://outlook.live.com/calendar/';
  return undefined;
}

/** A wire item on the reader's own clock. One that can't be placed is left out. */
export function fromWireItem(wire: WireCalendarItem): CalendarItem | undefined {
  const start = wire.allDay ? localDay(wire.allDay.startDate) : Date.parse(wire.start);
  const end = wire.allDay ? localDay(wire.allDay.endDate) : Date.parse(wire.end);
  if (!Number.isFinite(start)) return undefined;
  const { allDay: _dates, ...rest } = wire;
  const href = wire.href ?? (wire.kind === 'meeting' ? providerDayLink(wire.source?.provider, start) : undefined);
  return {
    ...rest,
    start,
    end: Number.isFinite(end) && end > start ? end : start + HOUR,
    allDay: !!wire.allDay,
    ...(href ? { href, external: true } : {}),
  };
}

/**
 * The same booking read from Bookings and from a calendar that Bookings syncs into shows once, as the
 * booking: it carries the client, staff and status the synced copy lacks. A synced copy is a meeting at
 * the same start and end whose title names the booking's service.
 */
export function withoutSyncedBookingCopies(meetings: CalendarItem[], bookings: CalendarItem[]): CalendarItem[] {
  if (bookings.length === 0) return meetings;
  const bySlot = new Map<string, string[]>();
  for (const booking of bookings) {
    const slot = `${booking.start}-${booking.end}`;
    bySlot.set(slot, [...(bySlot.get(slot) ?? []), booking.title.trim().toLowerCase()]);
  }
  return meetings.filter((meeting) => {
    const titles = bySlot.get(`${meeting.start}-${meeting.end}`);
    if (!titles) return true;
    const title = meeting.title.trim().toLowerCase();
    return !titles.some((booking) => booking && title.includes(booking));
  });
}

/** The fields of a task the calendar reads. `WebTask` satisfies it. */
export type CalendarTask = BucketableTask & {
  id: string;
  title?: string;
  workspaceId?: string;
  updatedDate?: string | Date;
  agentId?: string;
};

/**
 * Tasks with a date: one with `notBefore` sits at that moment, a finished one on the moment it
 * finished. A task with neither stays in Tasks — most have no date.
 */
export function taskItems(tasks: CalendarTask[]): CalendarItem[] {
  const items: CalendarItem[] = [];
  for (const task of tasks) {
    if (!task?.id) continue;
    const bucket = bucketOfTask(task);
    const settled = bucket === 'done' || bucket === 'failed';
    const finishedAt = task.updatedDate ? new Date(task.updatedDate).getTime() : NaN;
    const at = notBeforeMs(task) ?? (settled ? finishedAt : NaN);
    if (!Number.isFinite(at)) continue;
    items.push({
      key: `task:${task.id}`,
      kind: 'task',
      title: task.title || '',
      start: at,
      end: at,
      allDay: false,
      needsYou: bucket === 'waiting',
      settled,
      status: bucket,
      workspaceId: task.workspaceId || undefined,
      href: `/tasks/${encodeURIComponent(task.id)}`,
      ref: { taskId: task.id, agentId: task.agentId },
    });
  }
  return items;
}

/** The fields of a routine the calendar reads: a `WebRoutine` read with a window, which lists its runs. */
export interface CalendarRoutine {
  id: string;
  name?: string;
  workspaceId?: string;
  cron?: string;
  /** Its fire times in the window asked for, as ISO instants, by the runtime's own day rule. */
  runs?: string[];
  runsEveryDay?: boolean;
}

/** Every run of each routine inside the window it was read for. A daily one is marked so the month grid leaves it out. */
export function routineItems(routines: CalendarRoutine[]): CalendarItem[] {
  const items: CalendarItem[] = [];
  for (const routine of routines) {
    if (!routine?.id) continue;
    for (const run of routine.runs ?? []) {
      const at = Date.parse(run);
      if (!Number.isFinite(at)) continue;
      items.push({
        key: `routine:${routine.id}:${at}`,
        kind: 'routine',
        title: routine.name || '',
        start: at,
        end: at,
        allDay: false,
        repeatsDaily: routine.runsEveryDay === true,
        workspaceId: routine.workspaceId || undefined,
        ref: { routineId: routine.id, cron: routine.cron },
      });
    }
  }
  return items;
}
